import { getFetchHeaders } from '../utils/fetchHeaders';
import { injectJuicyScript } from '../utils/injectScript';
import { useEffect, useRef } from 'react';
import { useGetClientApplication } from '../api/atoms';
import { useNavigate } from 'react-router-dom';

interface JuicyInstance {
  getSessionId: () => string;
  getCompletedSessionId: (timeout: number) => string;
}

declare global {
  interface Window {
    juicyScoreApi: JuicyInstance;
  }
}

export const JUICY_SCRIPT =
  process.env.REACT_APP_JUICY_URL &&
  process.env.REACT_APP_JUICY_SCRIPT_KEY &&
  `${process.env.REACT_APP_JUICY_URL}&apiKey=${process.env.REACT_APP_JUICY_SCRIPT_KEY}`;

const NO_JUICY_SESSION_ID = 'No session id';

export const useJuicy = () => {
  const navigate = useNavigate();
  const { data: application, fetch: fetchApplication } =
    useGetClientApplication();

  const postJuicyRedirection = useRef<boolean>(false);

  useEffect(() => {
    injectJuicyScript();
  }, []);

  const callJuicyScore = async (
    application_id: number | undefined,
    session_id: string | undefined = NO_JUICY_SESSION_ID,
  ) => {
    if (!application_id) return;

    try {
      await fetch(`${process.env.REACT_APP_ANALYTICS_API}/juicyscore`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          application_id,
          session_id,
        }),
      });
    } catch {}

    return;
  };

  const generateJuicyApplicationSession = async ({
    nextRoute,
  }: {
    nextRoute?: string;
  } = {}) => {
    try {
      let juicySessionId: string | undefined;

      const newApplication =
        application ||
        (await fetchApplication({
          headers: getFetchHeaders({
            Accept: 'application/vnd.4finance.web.v1.hal+json',
            'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
          }),
        }));

      // In case juicy script injection fails
      setTimeout(async () => {
        if (!postJuicyRedirection.current) {
          await callJuicyScore(newApplication?.id, juicySessionId);
          if (nextRoute) navigate(nextRoute);
        }
      }, 8000);

      // Full method to get the completed session id
      try {
        juicySessionId = await window.juicyScoreApi.getSessionId(); // start raw data collection
        const juicyCompleteSessionId =
          await window.juicyScoreApi.getCompletedSessionId(10000); // getting the completed session id
        if (juicyCompleteSessionId) juicySessionId = juicyCompleteSessionId;
      } catch {
        // Catch juicy error to hide any possible error
      }

      await callJuicyScore(newApplication?.id, juicySessionId);
    } catch {
      // Catch juicy error to hide any possible error
    } finally {
      postJuicyRedirection.current = true;
      if (nextRoute) navigate(nextRoute);
    }
  };

  return {
    generateJuicyApplicationSession,
  };
};
